import { CapabilityCoordinatorBase, CapabilityRegistry } from '@trimble/web3d';
import {
  AlignmentNavigationCapability,
  CapabilityBase,
  FrustumCapability,
  ImageCapabilityBase,
  PanoramaCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import { createEffect, createMemo, on } from 'solid-js';

/**
 * Drives each pane's {@link FrustumCapability} `isActive()` from two inputs:
 *
 * 1. Auto-show: any pane has an active station-navigation, panorama, or
 *    path-navigation (during `Navigating`) capability, or a loaded image —
 *    all signals that the camera position should be visualised on the 2D map.
 * 2. A per-capability tri-state user override (`FrustumCapability.userIntent`)
 *    that survives pane registration changes:
 *      - `undefined` → follow auto-show
 *      - `true`      → force on regardless of auto-show
 *      - `false`     → force off regardless of auto-show
 *
 * The combined state is `userIntent() ?? autoShow`, so the user's explicit
 * toggle always wins for the pane they toggled, while auto-show continues to
 * drive panes the user hasn't touched.
 */
export class FrustumCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  constructor(registry: CapabilityRegistry) {
    super(registry);

    const frustumCapabilities = createMemo(() =>
      registry.getPaneCapabilities(FrustumCapability)
    );

    // `getPaneCapabilities` is an exact-class lookup and misses subclasses
    // (e.g. `MapStationCapability`, `PanoramaCapability`). Walk every pane's
    // capabilities and filter via `instanceof` so multipane scenes detect
    // active station/image/panorama views regardless of their concrete type.
    const allPaneCapabilities = createMemo<CapabilityBase[]>(() =>
      registry.paneContainers().flatMap((pc) => pc.capabilities())
    );

    const shouldShowFrustum = createMemo(() => {
      const caps = allPaneCapabilities();

      const isInStationView = caps.some(
        (c) => c instanceof StationsNavigationCapability && c.isActive()
      );

      const hasLoadedImage = caps.some(
        (c) => c instanceof ImageCapabilityBase && c.hasLoadedImage()
      );

      const isInPathNavigation = caps.some(
        (c) =>
          c instanceof AlignmentNavigationCapability &&
          c.isActive() &&
          c.status() === AlignmentNavigationStatus.Navigating
      );

      const isInPanoramaView = caps.some(
        (c) => c instanceof PanoramaCapability && c.isActive()
      );

      return (
        isInStationView ||
        isInPathNavigation ||
        hasLoadedImage ||
        isInPanoramaView
      );
    });

    // Track only `autoShow`, the capability list, and each pane's `userIntent`.
    // `on` runs the effect body untracked, so the imperative capability methods
    // (which read their own internal signals, e.g. `isActive()` guards) don't
    // register as dependencies and re-run the effect on every activation cycle.
    createEffect(
      on(
        () => {
          const autoShow = shouldShowFrustum();
          return frustumCapabilities().map((cap) => ({
            cap,
            shouldBeActive: cap.userIntent() ?? autoShow,
          }));
        },
        (entries) => {
          for (const { cap, shouldBeActive } of entries) {
            if (shouldBeActive) {
              cap.enable();
              cap.activate();
            } else {
              cap.deactivate();
            }
          }
        }
      )
    );
  }

  public cleanup(): Promise<void> {
    return Promise.resolve();
  }
}
